import {of as observableOf, combineLatest as observableCombineLatest, Observable} from 'rxjs';

import {map, debounceTime, takeWhile, catchError, first} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, inject} from '@angular/core';
import {ValidatorFn, Validators} from '@angular/forms';
import {uniqueValueValidator} from './validators/unique-validator.directive';
import {ResourceService} from '../../resource.service';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';
import {AbstractControlComponent} from './abstract-control';
import {ViewTabComponent} from '../viewtab.component';
import {ErrorService} from '../../../error/error.service';

@Component({
  selector: 'ctrl-textbox',
  template: `
    @if ((getCaughtObservable(isEnabled) | async) != true) {
      <p>{{ formControl?.value }}</p>
    }
    @if (getCaughtObservable(isEnabled) | async) {
      <mat-form-field floatLabel="never">
        @if (getCaughtObservable(useTextarea) | async) {
          <textarea
            matInput
            [placeholder]="(getCaughtObservable(_config?.Caption) | async)?.Values"
            (blur)="_parent.updateValidity(null)"
            [formControl]="formControl"
            [rows]="getCaughtObservable(rows) | async"
            autocomplete="off"
          ></textarea>
        }
        @if ((getCaughtObservable(useTextarea) | async) != true) {
          <input
            matInput
            [placeholder]="(getCaughtObservable(_config?.Caption) | async)?.Values"
            (blur)="_parent.updateValidity(null)"
            [formControl]="formControl"
            [maxlength]="maxLength"
            autocomplete="off"
          />
        }
        @if (formControl.hasError('uniqueValue')) {
          <mat-error>{{ getCaughtObservable(errorMessage_unique) | async }} </mat-error>
        }
        @if (formControl.hasError('required')) {
          <mat-error>
            <language key="ControlErrorRequired" defaultValue="This field is required."></language>
          </mat-error>
        }
        @if (formControl.hasError('pattern')) {
          <mat-error>
            @if (customPatternError === false) {
              <language
                key="CtrlTextboxErrorReqularExpression"
                defaultValue="Your input has to fulfill the regular expression {0}."
                [args]="[
                  getCaughtObservable(_config?.getPropertyValues('RegularExpression')) | async,
                ]"
              ></language>
            }
            @if (customPatternError !== false) {
              <span>{{ getCaughtObservable(customPatternError) | async }}</span>
            }
          </mat-error>
        }
        @if (formControl.errors?.maxlength) {
          <mat-error>
            <language
              key="CtrlTextboxErrorMaxLength"
              defaultValue="You mustn’t enter more than {0} characters."
              [args]="['' + maxLength]"
            ></language>
          </mat-error>
        }
      </mat-form-field>
    }
  `,
  styleUrls: ['./ctrl-textbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlTextboxComponent extends AbstractControlComponent {
  private envConfig = inject(EnvironmentConfigService);
  initialValue: string = null;
  maxLength: number = null;

  constructor(
    _parent: ViewTabComponent,
    private resourceService: ResourceService,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
  ) {
    super(_parent, 'Text', 'Text', changeDetector, _ngZone, errorService);
  }

  protected initValidators(): void {
    if (this.initialValue == null) {
      //check if sourceObs is null
      if (!(this.sourceObs === null || this.sourceObs === undefined)) {
        this.sourceObs.pipe(first()).subscribe(
          val => (this.initialValue = val),
          () => {},
        );
      }
    }

    observableCombineLatest(
      this._config.getBooleanPropertyValues('Required'),
      this._config.getPropertyValues('RegularExpression'),
      this._config.getPropertyValues('MaxLength'),
      (required: boolean, regex: string, maxLength: any) => {
        const validators: ValidatorFn[] = [];

        if (required === true) {
          validators.push(Validators.required);
        }

        if (regex) {
          validators.push(Validators.pattern(regex));
        }

        if (maxLength) {
          validators.push(Validators.maxLength(maxLength));
          this.maxLength = maxLength;
        }

        this.formControl.setValidators(validators);
        this.formControl.updateValueAndValidity({emitEvent: false});
      },
    )
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe();

    this._config
      .getPropertyValues('UniquenessValidationXPath')
      .pipe(takeWhile(() => this.alive))
      .subscribe(val => {
        if (val != null && val.length > 0) {
          this.formControl.setAsyncValidators([
            uniqueValueValidator(
              val,
              this.initialValue,
              this.resourceService,
              this.envConfig.getAttributeName('ObjectID'),
            ),
          ]);
        } else {
          this.formControl.clearAsyncValidators();
        }
        this.formControl.updateValueAndValidity({emitEvent: false});
      });
  }

  protected getValueChangedObs(): Observable<any> {
    return this.formControl.valueChanges.pipe(
      takeWhile(() => this.alive),
      debounceTime(300),
    );
  }

  get customPatternError(): false | Observable<string> {
    if (!this._config.propertyExists('RegularExpressionErrorMessage')) return false;
    return this._config.getPropertyValues('RegularExpressionErrorMessage');
  }

  get rows(): Observable<number> {
    if (this._config == null) return observableOf(0);
    return this._config.getPropertyValues('Rows').pipe(map(value => (value == null ? 0 : value)));
  }

  get useTextarea(): Observable<boolean> {
    return this.rows.pipe(map(value => value > 1));
  }

  get errorMessage_unique(): Observable<string> {
    return this._config.getPropertyValues('UniquenessErrorMessage').pipe(
      map(value => {
        if (value != null && typeof value === 'string' && value.length > 0) {
          return value.replace('%VALUE%', this.formControl.value);
        } else {
          let msg: string = '%VALUE% already exists. Please try a different one.';
          return msg.replace('%VALUE%', this.formControl.value);
        }
      }),
    );
  }
}
